import type {
  Dataset,
  DatasetCatalogReviewState,
  DatasetFolderRef,
  DatasetLabel,
  Embodiment,
  MemberRef,
  MemoPreview,
  Tip,
} from "@/api";
import { TableRow, type TableRowActionBuilder } from "@carbon6/c6-frontend-design";
import { formatEndEffector } from "@/components/embodiment";
import { formatArmLayout } from "@/components/filter/datasetFilters";

/**
 * 폴더 참조를 표시 경로로 바꾼다. 루트는 `Picking`, 자식은 `Picking / 2026-Q1` 이다.
 *
 * 트리가 2계층이라 `parent` 한 단계로 경로가 끝난다. 미분류(null)는 여기서 판정하지
 * 않고 그대로 null 을 반환한다. "폴더가 없다" 는 표시 문구는 소비처가 정한다.
 *
 * `== null` 은 의도한 느슨한 비교다(`===` 로 바꾸지 않는다). 배포 순서상 frontend 가 app 보다
 * 먼저 배포되는 구간이 있다. 그 구간의 이전 BE 응답에는 `folder` **키 자체가 없다**. `undefined`
 * 가 여기를 통과하면 아래 `folder.parent` 에서 목록 전체의 로드가 실패한다.
 */
export function formatFolderPath(folder: DatasetFolderRef | null): string | null {
  // @compat ZERG-549 until=be:e5443136
  if (folder == null) {
    return null;
  }
  return folder.parent === null
    ? folder.display_name
    : `${folder.parent.display_name} / ${folder.display_name}`;
}

export type DatasetTableRowActionBuilder = TableRowActionBuilder<DatasetTableRow>;
export type DatasetTableRowClickHandler = (row: DatasetTableRow) => void;

export class DatasetTableRow extends TableRow implements Dataset {
  readonly uuid: string;
  readonly organization_uuid: string;
  readonly artifact_source_uuid: string;
  readonly storage_backend: Dataset["storage_backend"];
  readonly training_supported: boolean;
  readonly embodiment: Embodiment;
  readonly arm_layout: Dataset["arm_layout"];
  readonly tip: Tip | null;
  readonly display_name: string;
  readonly operator_member: MemberRef | null;
  readonly status: Dataset["status"];
  readonly processing: Dataset["processing"];
  readonly review_state: DatasetCatalogReviewState;
  readonly reviewed: boolean;
  readonly schema_version: Dataset["schema_version"];
  readonly recording_mode: Dataset["recording_mode"];
  readonly recording_mode_modified_manually: boolean;
  readonly data_origin: Dataset["data_origin"];
  readonly total_episodes: number;
  readonly quality_candidate_pending_count: Dataset["quality_candidate_pending_count"];
  readonly missing_channel_episode_count: number;
  // 길이·용량 컬럼이 그리는 값. null 은 아직 측정하지 않았다는 뜻이라 0 과 구분해 표시한다.
  readonly median_episode_length_sec: number | null;
  readonly total_duration_sec: number | null;
  readonly total_size_bytes: number | null;
  // 실측 카메라 스트림 수와 schema 외(extra) 파일 수. 리뷰 큐 컬럼 표시에 쓴다.
  readonly camera_stream_count: number;
  readonly extra_file_count: number;
  readonly created_at: string;
  readonly modified_at: string;
  readonly labels: readonly DatasetLabel[];
  readonly thumbnail_url: string | null;
  readonly thumbnail_url_expires_at: string | null;
  readonly representative_episode_file_uuid: string | null;
  // 정체성 facet 컬럼 표시값. embodiment 객체와 실측 팔 구성에서 계산한다(라벨에서 계산하지 않는다).
  readonly embodimentName: string;
  readonly armsLabel: string | null;
  readonly endEffectorName: string | null;
  // 메모(댓글) 요약. 목록 카드와 표의 표시기에 쓴다.
  readonly memo_count: number;
  readonly latest_memo: MemoPreview | null;
  // 놓인 폴더(미분류면 null). `폴더` 컬럼이 그대로 그린다.
  readonly folder: DatasetFolderRef | null;
  // 폴더 컬럼 표시값. 2계층이라 `부모 / 자식` 으로 경로가 끝난다. 미분류는 null 이고,
  // 그때 컬럼이 `미분류` 를 대신 그린다(빈 칸과 구분한다).
  readonly folderPath: string | null;
  // 요청자 기준 쓰기 자격(서버가 계산). 선택 액션의 보관이 선택 전체에 대해 이 값을 읽는다.
  readonly can_write: boolean;
  private readonly rowClickHandler: DatasetTableRowClickHandler | null;

  constructor(
    dataset: Dataset,
    rowActionsBuilder: DatasetTableRowActionBuilder | null = null,
    rowClickHandler: DatasetTableRowClickHandler | null = null,
  ) {
    super(rowActionsBuilder ? (row) => rowActionsBuilder(row as DatasetTableRow) : null);
    this.rowClickHandler = rowClickHandler;
    this.uuid = dataset.uuid;
    this.organization_uuid = dataset.organization_uuid;
    this.artifact_source_uuid = dataset.artifact_source_uuid;
    this.storage_backend = dataset.storage_backend;
    this.training_supported = dataset.training_supported;
    this.embodiment = dataset.embodiment;
    this.arm_layout = dataset.arm_layout;
    this.tip = dataset.tip;
    this.display_name = dataset.display_name;
    this.operator_member = dataset.operator_member;
    this.status = dataset.status;
    this.processing = dataset.processing;
    this.review_state = dataset.review_state;
    this.reviewed = dataset.reviewed;
    this.schema_version = dataset.schema_version;
    this.recording_mode = dataset.recording_mode;
    this.recording_mode_modified_manually = dataset.recording_mode_modified_manually;
    this.data_origin = dataset.data_origin;
    this.total_episodes = dataset.total_episodes;
    this.quality_candidate_pending_count = dataset.quality_candidate_pending_count;
    this.missing_channel_episode_count = dataset.missing_channel_episode_count;
    this.median_episode_length_sec = dataset.median_episode_length_sec;
    this.total_duration_sec = dataset.total_duration_sec;
    this.total_size_bytes = dataset.total_size_bytes;
    this.camera_stream_count = dataset.camera_stream_count;
    this.extra_file_count = dataset.extra_file_count;
    this.created_at = dataset.created_at;
    this.modified_at = dataset.modified_at;
    this.labels = dataset.labels ?? [];
    this.thumbnail_url = dataset.thumbnail_url;
    this.thumbnail_url_expires_at = dataset.thumbnail_url_expires_at;
    this.representative_episode_file_uuid = dataset.representative_episode_file_uuid;
    this.embodimentName = dataset.embodiment.name;
    this.armsLabel = formatArmLayout(dataset.arm_layout);
    this.endEffectorName = dataset.embodiment.end_effector?.name
      ? formatEndEffector(dataset.embodiment.end_effector)
      : null;
    this.memo_count = dataset.memo_count;
    this.latest_memo = dataset.latest_memo;
    this.folder = dataset.folder;
    this.folderPath = formatFolderPath(dataset.folder);
    this.can_write = dataset.can_write;
  }

  getRowId(): string {
    return this.uuid;
  }

  // 모든 행을 표시한다. 가시성은 서버 조회가 정한다.
  canShow(): boolean {
    return true;
  }

  // 체크박스 선택 대상은 모든 행이다(상한 등 수용량은 source 가 담당한다).
  canSelect(): boolean {
    return true;
  }

  get isRowClickable(): boolean {
    return this.rowClickHandler !== null;
  }

  onRowClick(): void {
    this.rowClickHandler?.(this);
  }
}

export function createDatasetTableRow(
  dataset: Dataset,
  rowActionsBuilder: DatasetTableRowActionBuilder | null = null,
  rowClickHandler: DatasetTableRowClickHandler | null = null,
): DatasetTableRow {
  return new DatasetTableRow(dataset, rowActionsBuilder, rowClickHandler);
}

export function createDatasetTableRows(
  datasets: readonly Dataset[],
  rowActionsBuilder: DatasetTableRowActionBuilder | null = null,
  rowClickHandler: DatasetTableRowClickHandler | null = null,
): DatasetTableRow[] {
  return datasets.map((dataset) =>
    createDatasetTableRow(dataset, rowActionsBuilder, rowClickHandler),
  );
}
